import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner,
  Badge,
  Pagination,
  Dropdown,
  DropdownButton,
  InputGroup
} from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiEye, FiList, FiPlus, FiMinus, FiPackage } from 'react-icons/fi';
import { customersAPI, systemsAPI, productsAPI, gatewaysAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Customer, CustomersResponse, System, SystemsResponse, Product, Gateway } from '../types';

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showSystemsListModal, setShowSystemsListModal] = useState(false);
  const [showManageProductsModal, setShowManageProductsModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedSystemForProducts, setSelectedSystemForProducts] = useState<System | null>(null);
  const [customerSystems, setCustomerSystems] = useState<System[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [gateways, setGateways] = useState<Gateway[]>([]);
  // Edit-in-place state for the Add Product form
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productFormData, setProductFormData] = useState({
    productId: '',
    quantity: 1,
    metadata: {
      companyId: '',
      gateway: '',
      productType: '',
      locationLabel: '',
      itemCode: '',
      itemSerialNumber: '',
      productNameOverride: '',
      label: '',
      specifications: '',
      calculationFormula: '',
      parameters: [
        { name: '', unit: '', dividingFactor: undefined as number | undefined, systemType: '', pIndex: undefined as number | undefined, showOnGraph: false }
      ]
    }
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    primaryContact: '',
    loginId: '',
    password: '',
    dailyReportEmail: '',
    dailyReportEmail2: '',
    amcStartDate: '',
    amcEndDate: '',
    address: ''
  });

  // System form state
  const [systemFormData, setSystemFormData] = useState({
    systemName: '',
    systemCode: '',
    description: '',
    location: '',
    primaryProductId: '',
    gatewayId: '',
    installationDate: new Date().toISOString().split('T')[0]
  });

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response: CustomersResponse = await customersAPI.getAll(currentPage, 10);
      setCustomers(response.customers);
      setTotalPages(response.pagination.total);
    } catch (error) {
      setError('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchCustomers();
    fetchDropdownData();
  }, [fetchCustomers]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [productsRes, gatewaysRes] = await Promise.all([
        productsAPI.getAll(1, 100),
        gatewaysAPI.getAll(1, 100)
      ]);
      
      setProducts(productsRes.products);
      setGateways(gatewaysRes.gateways);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  }, []);

  const fetchCustomerSystems = async (customerId: string) => {
    try {
      const response: SystemsResponse = await systemsAPI.getAll(1, 100, '', customerId);
      setCustomerSystems(response.systems);
    } catch (error) {
      console.error('Failed to fetch customer systems:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      email: '',
      phone: '',
      primaryContact: '',
      loginId: '',
      password: '',
      dailyReportEmail: '',
      dailyReportEmail2: '',
      amcStartDate: '',
      amcEndDate: '',
      address: ''
    });
    setEditingCustomer(null);
  };

  const resetSystemForm = () => {
    setSystemFormData({
      systemName: '',
      systemCode: '',
      description: '',
      location: '',
      primaryProductId: '',
      gatewayId: '',
      installationDate: new Date().toISOString().split('T')[0]
    });
  };

  const handleShowModal = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        companyName: customer.companyName,
        email: customer.email,
        phone: customer.phone,
        primaryContact: customer.primaryContact,
        loginId: customer.loginId,
        password: '',
        dailyReportEmail: customer.dailyReportEmail,
        dailyReportEmail2: customer.dailyReportEmail2,
        amcStartDate: new Date(customer.amcStartDate).toISOString().split('T')[0],
        amcEndDate: new Date(customer.amcEndDate).toISOString().split('T')[0],
        address: customer.address
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const customerData = {
        ...formData,
        amcStartDate: new Date(formData.amcStartDate),
        amcEndDate: new Date(formData.amcEndDate),
        isActive: true
      };

      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, customerData);
        setSuccess('Customer updated successfully');
      } else {
        await customersAPI.create(customerData);
        setSuccess('Customer created successfully');
      }
      
      fetchCustomers();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Operation failed');
    }
  };

  const handleToggleStatus = async (customerId: string) => {
    try {
      await customersAPI.toggleStatus(customerId);
      fetchCustomers();
    } catch (error) {
      setError('Failed to toggle customer status');
    }
  };

  const handleDelete = async (customerId: string) => {
    if (window.confirm('Are you sure you want to delete this customer? This will also delete all associated users.')) {
      try {
        await customersAPI.delete(customerId);
        fetchCustomers();
      } catch (error) {
        setError('Failed to delete customer');
      }
    }
  };

  // System management handlers
  const handleAddSystem = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSystemFormData({
      systemName: '',
      systemCode: '',
      description: '',
      location: '',
      primaryProductId: '',
      gatewayId: '',
      installationDate: new Date().toISOString().split('T')[0]
    });
    await fetchDropdownData();
    setShowSystemModal(true);
  };

  const handleViewSystems = async (customer: Customer) => {
    setSelectedCustomer(customer);
    await fetchCustomerSystems(customer.id);
    setShowSystemsListModal(true);
  };

  const handleShowManageProducts = async (system: System) => {
    setSelectedSystemForProducts(system);
  setProductFormData({
      productId: '',
      quantity: 1,
      metadata: {
        companyId: selectedCustomer?.companyName || '',
        gateway: typeof system.gatewayId === 'object' ? ((system.gatewayId as any).id || (system.gatewayId as any)._id) : (system.gatewayId as any) || '',
        productType: '',
        locationLabel: (system as any).location || '',
        itemCode: '',
        itemSerialNumber: '',
        productNameOverride: '',
        label: '',
        specifications: '',
        calculationFormula: '',
        parameters: [
          { name: '', unit: '', dividingFactor: undefined, systemType: '', pIndex: undefined, showOnGraph: false }
        ]
      }
    });
    // Ensure products list available
    if (products.length === 0) {
      await fetchDropdownData();
    }
    setShowManageProductsModal(true);
  };

  const handleCloseManageProducts = () => {
    setShowManageProductsModal(false);
    setSelectedSystemForProducts(null);
  setProductFormData({
      productId: '',
      quantity: 1,
      metadata: {
        companyId: '',
        gateway: '',
        productType: '',
        locationLabel: '',
        itemCode: '',
        itemSerialNumber: '',
        productNameOverride: '',
        label: '',
        specifications: '',
        calculationFormula: '',
        parameters: [
          { name: '', unit: '', dividingFactor: undefined, systemType: '', pIndex: undefined, showOnGraph: false }
        ]
      }
    });
    setError('');
    setSuccess('');
  };

  const handleAddSystemProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSystemForProducts) return;
    try {
      // No pIndex uniqueness validation enforced anymore.

      if (editingProductId) {
        // Update existing product (don't change productId)
        await systemsAPI.updateProduct(
          selectedSystemForProducts.id,
          editingProductId,
          { quantity: productFormData.quantity, metadata: productFormData.metadata } as any
        );
        setSuccess('Product updated successfully');
        setEditingProductId(null);
        // Close modal after successful update
        handleCloseManageProducts();
      } else {
        // Add new product
        await systemsAPI.addProduct(selectedSystemForProducts.id, productFormData as any);
        setSuccess('Product added to system successfully');
        // Close modal after successful add
        handleCloseManageProducts();
      }
      if (selectedCustomer) {
        await fetchCustomerSystems(selectedCustomer.id);
        // Update the selected system reference with fresh data
        const updated = customerSystems.find(s => s.id === selectedSystemForProducts.id);
        if (updated) setSelectedSystemForProducts(updated);
      }
      setProductFormData(prev => ({
        productId: '',
        quantity: 1,
        metadata: prev.metadata
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to add product');
      // Close modal on error as requested
      handleCloseManageProducts();
    }
  };

  // Memoized: pIndex values already used in this system by other products (exclude the one being edited)
  // pIndex usage tracking removed — no validation enforced

  // Dynamic parameter controls for Manage Products modal
  const addParameter = () => {
    setProductFormData(prev => {
      const next = { ...prev } as any;
      next.metadata.parameters = [
        ...next.metadata.parameters,
        { name: '', unit: '', dividingFactor: undefined, systemType: '', pIndex: undefined, showOnGraph: false }
      ];
      return next;
    });
  };

  const removeParameter = (idx: number) => {
    setProductFormData(prev => {
      const next = { ...prev } as any;
      next.metadata.parameters = next.metadata.parameters.filter((_: any, i: number) => i !== idx);
      return next;
    });
  };

  const handleRemoveSystemProduct = async (productId: string) => {
    if (!selectedSystemForProducts) return;
    try {
      // productId here is the additionalProducts subdocument id
      await systemsAPI.removeProduct(selectedSystemForProducts.id, productId);
      setSuccess('Product removed from system');

      // Optimistically update selected system products and total count
      setSelectedSystemForProducts(prev => {
        if (!prev) return prev;
        const filtered = (prev.additionalProducts || []).filter(p => {
          const subdocId = (p as any)._id || (p as any).id;
          return String(subdocId) !== String(productId);
        });
        const additionalQty = filtered.reduce((sum, p: any) => sum + (Number(p.quantity) || 0), 0);
        return { ...prev, additionalProducts: filtered as any, totalProducts: 1 + additionalQty } as System;
      });

      if (selectedCustomer) {
        await fetchCustomerSystems(selectedCustomer.id);
        const updated = customerSystems.find(s => s.id === selectedSystemForProducts.id);
        if (updated) setSelectedSystemForProducts(updated);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove product');
    }
  };

  const handleStartEditSystemProduct = (p: any) => {
    // use the additionalProducts subdocument id as the editing identifier
    const subdocId = (p as any)._id || (p as any).id;
    const pid = (p.productId != null && typeof p.productId === 'object') ? ((p.productId as any).id || (p.productId as any)._id) : p.productId;
    setEditingProductId(String(subdocId));
    // normalize gateway to an id if possible
    let gatewayValue = p.metadata?.gateway || '';
    if (gatewayValue && gateways && gateways.length > 0) {
      const found = gateways.find((g) => g.id === gatewayValue || g.gatewayName === gatewayValue || String((g.customerId as any)?.id || g.customerId) === gatewayValue);
      if (found) gatewayValue = found.id;
    }

    setProductFormData({
      productId: String(pid),
      quantity: p.quantity || 1,
      metadata: {
        companyId: p.metadata?.companyId || '',
        gateway: gatewayValue || '',
        productType: p.metadata?.productType || '',
        locationLabel: p.metadata?.locationLabel || '',
        itemCode: p.metadata?.itemCode || '',
        itemSerialNumber: p.metadata?.itemSerialNumber || '',
        productNameOverride: p.metadata?.productNameOverride || '',
        label: p.metadata?.label || '',
        specifications: p.metadata?.specifications || '',
        calculationFormula: (p as any).calculationFormula || p.metadata?.calculationFormula || '',
        parameters: (p.metadata?.parameters || []).map((pr: any) => ({
          name: pr?.name || '',
          unit: pr?.unit || '',
          dividingFactor: typeof pr?.dividingFactor === 'number' ? pr.dividingFactor : undefined,
          systemType: pr?.systemType || '',
          pIndex: typeof pr?.pIndex === 'number' ? pr.pIndex : (typeof pr?.pindex === 'number' ? pr.pindex : undefined),
          showOnGraph: typeof pr?.showOnGraph === 'boolean' ? pr.showOnGraph : false
        }))
      }
    });
  };

  const handleCloseSystemModal = () => {
    setShowSystemModal(false);
    setSelectedCustomer(null);
    setSystemFormData({
      systemName: '',
      systemCode: '',
      description: '',
      location: '',
      primaryProductId: '',
      gatewayId: '',
      installationDate: new Date().toISOString().split('T')[0]
    });
  };

  const handleCloseSystemsListModal = () => {
    setShowSystemsListModal(false);
    setSelectedCustomer(null);
    setCustomerSystems([]);
  };

  const handleSystemInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSystemFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSystemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const systemData = {
        ...systemFormData,
        customerId: selectedCustomer?.id,
        installationDate: new Date(systemFormData.installationDate)
      };
      await systemsAPI.create(systemData);
      setSuccess('System created successfully');
      handleCloseSystemModal();
      // Refresh customer systems if list modal is open
      if (selectedCustomer) {
        fetchCustomerSystems(selectedCustomer.id);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create system');
    }
  };

  const getAMCStatusBadge = (amcStatus: string) => {
    const variants = {
      active: 'success',
      expired: 'danger',
      upcoming: 'warning'
    };
    return <Badge bg={variants[amcStatus as keyof typeof variants] || 'secondary'}>
      {amcStatus.toUpperCase()}
    </Badge>;
  };

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) return customers;
    const search = searchTerm.toLowerCase();
    return customers.filter((customer) =>
      customer.companyName?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.phone?.toLowerCase().includes(search) ||
      customer.primaryContact?.toLowerCase().includes(search)
    );
  }, [customers, searchTerm]);

  if (loading && customers.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading customers...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <div style={{ width: '95%', maxWidth: '1800px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <div className="gradient-card p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="mb-2 fw-bold">Customers Management</h2>
              <p className="mb-0 opacity-90">Manage customer accounts, AMC, and systems</p>
            </div>
            <Button variant="primary" onClick={() => handleShowModal()} className="btn-gradient btn-gradient-primary">
              <FiPlus size={16} className="me-2" />Add New Customer
            </Button>
          </div>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')} className="modern-card">{error}</Alert>}

      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <div className="mb-3">
                <InputGroup>
                  <InputGroup.Text>🔍</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by company name, email, phone, or contact..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setSearchTerm('')}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
              </div>
              <div className="table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Primary Contact</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>AMC Status</th>
                      <th>Users</th>
                      <th>Systems</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((customer) => (
                      <tr key={customer.id}>
                        <td>{customer.companyName}</td>
                        <td>{customer.primaryContact}</td>
                        <td>{customer.email}</td>
                        <td>{customer.phone}</td>
                        <td>{getAMCStatusBadge(customer.amcStatus)}</td>
                        <td>{customer.totalUsers}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleAddSystem(customer)}
                            >
                              Add System
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleViewSystems(customer)}
                            >
                              <FiList size={14} className="me-1" />
                              View Systems
                            </Button>
                          </div>
                        </td>
                        <td>
                          <Badge bg={customer.isActive ? 'success' : 'danger'}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(customer)}
                            title="Edit customer"
                            aria-label="Edit customer"
                          >
                            <FiEdit2 size={14} />
                          </Button>
                          <Button
                            variant={customer.isActive ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            className="me-1"
                            onClick={() => handleToggleStatus(customer.id)}
                          >
                            {customer.isActive ? <><FiUserX size={14} className="me-1" /></> : <><FiUserCheck size={14} className="me-1" />Activate</>}
                          </Button>
                          <Button
                            variant="outline-dark"
                            size="sm"
                            title="View as customer"
                            aria-label="View as customer"
                            onClick={() => navigate(`/customers/${customer.id}/preview`) }
                          >
                            <FiEye size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(customer.id)}
                            title="Delete customer"
                            aria-label="Delete customer"
                          >
                            <FiTrash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {filteredCustomers.length === 0 && customers.length > 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No customers match your search. Try different keywords.</p>
                </div>
              )}
              {customers.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No customers found. Click "Add New Customer" to create one.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Customer Form Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton className="gradient-card-header border-0">
          <Modal.Title>
            {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Contact *</Form.Label>
                  <Form.Control
                    type="text"
                    name="primaryContact"
                    value={formData.primaryContact}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Login ID *</Form.Label>
                  <Form.Control
                    type="text"
                    name="loginId"
                    value={formData.loginId}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password {!editingCustomer && '*'}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingCustomer}
                    placeholder={editingCustomer ? 'Leave empty to keep current password' : ''}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Daily Report Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="dailyReportEmail"
                    value={formData.dailyReportEmail}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Daily Report Email 2 *</Form.Label>
                  <Form.Control
                    type="email"
                    name="dailyReportEmail2"
                    value={formData.dailyReportEmail2}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>AMC Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="amcStartDate"
                    value={formData.amcStartDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>AMC End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="amcEndDate"
                    value={formData.amcEndDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCustomer ? 'Update Customer' : 'Create Customer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add System Modal */}
      <Modal show={showSystemModal} onHide={handleCloseSystemModal} size="lg">
        <Form onSubmit={handleSystemSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add System for {selectedCustomer?.companyName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>System Name *</Form.Label>
              <Form.Control
                type="text"
                name="systemName"
                value={systemFormData.systemName}
                onChange={handleSystemInputChange}
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>System Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="systemCode"
                    value={systemFormData.systemCode}
                    onChange={handleSystemInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={systemFormData.location}
                    onChange={handleSystemInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={systemFormData.description}
                onChange={handleSystemInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Product *</Form.Label>
                  <Form.Select
                    name="primaryProductId"
                    value={systemFormData.primaryProductId}
                    onChange={handleSystemInputChange}
                    required
                  >
                    <option value="">Select Primary Product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.productName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Gateway *</Form.Label>
                  <Form.Select
                    name="gatewayId"
                    value={systemFormData.gatewayId}
                    onChange={handleSystemInputChange}
                    required
                  >
                    <option value="">Select Gateway</option>
                    {gateways.map((gateway) => (
                      <option key={gateway.id} value={gateway.id}>
                        {gateway.gatewayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Installation Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="installationDate"
                    value={systemFormData.installationDate}
                    onChange={handleSystemInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseSystemModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create System
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Systems Modal */}
      <Modal show={showSystemsListModal} onHide={handleCloseSystemsListModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Systems for {selectedCustomer?.companyName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customerSystems.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                    <tr>
                      <th>System Name</th>
                      <th>Primary Product</th>
                      <th>Gateway</th>
                      <th>Total Products</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {customerSystems.map((system) => (
                    <tr key={system.id}>
                      <td>{system.systemName}</td>
                        <td>
                          {system.primaryProductId
                            ? (typeof system.primaryProductId === 'object'
                                ? ((system.primaryProductId as any).productName
                                  || (system.primaryProductId as any).id
                                  || (system.primaryProductId as any)._id)
                                : system.primaryProductId)
                            : 'N/A'}
                        </td>
                        <td>
                          {system.gatewayId
                            ? (typeof system.gatewayId === 'object'
                                ? ((system.gatewayId as any).gatewayName
                                  || (system.gatewayId as any).id
                                  || (system.gatewayId as any)._id)
                                : system.gatewayId)
                            : 'N/A'}
                        </td>
                      <td>
                        <Badge bg="info">{system.totalProducts}</Badge>
                      </td>
                      <td>{new Date(system.createdAt).toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          onClick={() => handleShowManageProducts(system)}
                        >
                          Manage Products
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this system?')) {
                              try {
                                await systemsAPI.delete(system.id);
                                setSuccess('System deleted successfully');
                                if (selectedCustomer) {
                                  fetchCustomerSystems(selectedCustomer.id);
                                }
                              } catch (error: any) {
                                setError(error.message || 'Failed to delete system');
                              }
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">No systems found for this customer.</p>
              <Button
                variant="primary"
                onClick={() => {
                  handleCloseSystemsListModal();
                  handleAddSystem(selectedCustomer!);
                }}
              >
                Add First System
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSystemsListModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseSystemsListModal();
              handleAddSystem(selectedCustomer!);
            }}
          >
            Add New System
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manage Products Modal */}
  <Modal show={showManageProductsModal} onHide={handleCloseManageProducts} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Products {selectedSystemForProducts ? `for ${selectedSystemForProducts.systemName}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {/* Existing products list */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6">Existing Products</Card.Title>
              {selectedSystemForProducts && selectedSystemForProducts.additionalProducts && selectedSystemForProducts.additionalProducts.length > 0 ? (
                <div className="table-responsive">
                  <Table size="sm" bordered hover>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Calculation</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSystemForProducts!.additionalProducts.map((p, idx) => (
                        <tr key={(p as any)._id || idx}>
                          <td>{p.productId != null && typeof p.productId === 'object' ? p.productId.productName : p.productId}</td>
                          <td>{p.quantity}</td>
                          <td>{(p as any).calculationFormula || p.metadata?.calculationFormula || '-'}</td>
                          <td className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleStartEditSystemProduct(p)}
                              title="Edit product entry"
                              aria-label="Edit product entry"
                            >
                              <FiEdit2 size={14} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveSystemProduct((p as any)._id)}
                              title="Remove product entry"
                              aria-label="Remove product entry"
                            >
                              <FiTrash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted mb-0">No additional products added yet.</p>
              )}
            </Card.Body>
          </Card>

          {/* Add product form */}
          <Card>
            <Card.Body>
              <Card.Title className="h6">Add Product</Card.Title>
              <Form onSubmit={handleAddSystemProduct}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Label>Product *</Form.Label>
                    <Form.Select
                      name="productId"
                      value={productFormData.productId}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, productId: e.target.value }))}
                      required
                      disabled={!!editingProductId}
                    >
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>{product.productName}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={productFormData.quantity}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      required
                    />
                  </Col>
                </Row>

                {/* Additional IoT/legacy metadata fields */}
                <Row className="mt-3 g-3">
                  <Col md={6}>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.companyId}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, companyId: e.target.value } }))}
                      placeholder="Company ID / Name"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Gateway</Form.Label>
                    <Form.Select
                      value={productFormData.metadata.gateway}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, gateway: e.target.value } }))}
                    >
                      <option value="">Select Gateway</option>
                      {gateways
                        .filter((g) => {
                          const gid = typeof (g.customerId as any) === 'object' ? ((g.customerId as any).id || (g.customerId as any)._id) : (g.customerId as any);
                          return selectedCustomer ? String(gid) === String(selectedCustomer.id) : false;
                        })
                        .map((g) => (
                          <option key={g.id} value={g.id}>{g.gatewayName}</option>
                        ))}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="mt-2 g-3">
                  <Col md={12}>
                    <Form.Label>Calculation Formula</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.calculationFormula}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, calculationFormula: e.target.value } }))}
                      placeholder="e.g., (p1 * 2) + p2"
                    />
                  </Col>
                </Row>

                <Row className="mt-2 g-3">
                  <Col md={6}>
                    <Form.Label>Product Type</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.productType}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, productType: e.target.value } }))}
                      placeholder="e.g., TDS"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Location Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.locationLabel}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, locationLabel: e.target.value } }))}
                      placeholder="Location"
                    />
                  </Col>
                </Row>

                <Row className="mt-2 g-3">
                  <Col md={6}>
                    <Form.Label>Item Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.itemCode}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, itemCode: e.target.value } }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Item Serial No</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.itemSerialNumber}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, itemSerialNumber: e.target.value } }))}
                    />
                  </Col>
                </Row>

                <Row className="mt-2 g-3">
                  <Col md={6}>
                    <Form.Label>Product Name (override)</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.productNameOverride}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, productNameOverride: e.target.value } }))}
                      placeholder="Defaults to selected product"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={productFormData.metadata.label}
                      onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, label: e.target.value } }))}
                    />
                  </Col>
                </Row>

                {/* Parameter blocks */}
                <div className="mt-3">
                  {productFormData.metadata.parameters.map((p: any, idx: number) => (
                    <Card className="mb-3" key={idx}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <Card.Title className="h6 mb-0">Parameter {idx + 1}</Card.Title>
                          {productFormData.metadata.parameters.length > 1 && (
                            <Button variant="outline-danger" size="sm" onClick={() => removeParameter(idx)}>Remove</Button>
                          )}
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Label>Label</Form.Label>
                            <Form.Control
                              type="text"
                              value={p.name || ''}
                              onChange={(e) => setProductFormData(prev => {
                                const next = { ...prev } as any;
                                next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], name: e.target.value };
                                return next;
                              })}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label>Unit</Form.Label>
                            <Form.Control
                              type="text"
                              value={p.unit || ''}
                              onChange={(e) => setProductFormData(prev => {
                                const next = { ...prev } as any;
                                next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], unit: e.target.value };
                                return next;
                              })}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label>Dividing Factor</Form.Label>
                            <Form.Control
                              type="number"
                              value={p.dividingFactor ?? ''}
                              onChange={(e) => setProductFormData(prev => {
                                const val = e.target.value === '' ? undefined : Number(e.target.value);
                                const next = { ...prev } as any;
                                next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], dividingFactor: val };
                                return next;
                              })}
                              min="0"
                              step="any"
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label>Device Field</Form.Label>
                            <Form.Select
                              value={p.pIndex ?? ''}
                              onChange={(e) => setProductFormData(prev => {
                                const val = e.target.value === '' ? undefined : Number(e.target.value);
                                const next = { ...prev } as any;
                                next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], pIndex: val };
                                return next;
                              })}
                            >
                              <option value="">Select p-key</option>
                              {Array.from({ length: 30 }, (_, i) => i + 1).map(n => (
                                <option key={n} value={n}>{`p${n}`}</option>
                              ))}
                            </Form.Select>
                            {/* pIndex conflicts are not enforced; any p-key may be selected. */}
                          </Col>
                          <Col md={6}>
                            <Form.Label>System type</Form.Label>
                            <Form.Control
                              type="text"
                              value={p.systemType || ''}
                              onChange={(e) => setProductFormData(prev => {
                                const next = { ...prev } as any;
                                next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], systemType: e.target.value };
                                return next;
                              })}
                            />
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={12}>
                            <Form.Check
                              type="checkbox"
                              id={`param-show-graph-${idx}`}
                              label="Show on Graph"
                              checked={Boolean(p.showOnGraph)}
                              onChange={(e) => setProductFormData(prev => {
                                const next = { ...prev } as any;
                                next.metadata.parameters = next.metadata.parameters.map((en: any, i: number) => i === idx ? { ...en, showOnGraph: e.target.checked } : en);
                                return next;
                              })}
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                  <div className="d-flex justify-content-end">
                    <Button variant="outline-primary" size="sm" onClick={addParameter}>+ Add Parameter</Button>
                  </div>
                </div>

                <Form.Group className="mt-3">
                  <Form.Label>Specifications</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={productFormData.metadata.specifications}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, specifications: e.target.value } }))}
                    placeholder="Enter specifications..."
                  />
                </Form.Group>

                <div className="mt-3 d-flex justify-content-end gap-2">
                  {editingProductId && (
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => {
                        setEditingProductId(null);
                        setProductFormData(prev => ({
                          productId: '',
                          quantity: 1,
                          metadata: prev.metadata
                        }));
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button type="submit" variant="primary" className="btn-gradient btn-gradient-primary">{editingProductId ? 'Update' : 'Add'}</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={handleCloseManageProducts}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* unified add/edit uses the same form above */}
      </div>
    </Container>
  );
};

export default Customers;